import { useState } from "react";
import { useApiData, fmtDate, fmtDateTime } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/common/StatusBadge";
import { PageTitle, EmptyState } from "@/components/shell/DashboardShell";
import { AttachmentList, DetailGrid } from "@/components/portal/RequestParts";
import { ExportButton, FilterBar, Pill, useQueryPath } from "@/components/admin/FilterBar";
import { StatusUpdater } from "@/components/admin/StatusUpdater";
import { ENQUIRY_STATUSES, STATUS } from "@/data/portal";

const KINDS = { all: "All", quote: "Quick quote", bulk: "Bulk", amc: "AMC", contact: "Contact" };

const EnquiryDialog = ({ e, onClose, onSaved }) => (
  <Dialog open onOpenChange={onClose}>
    <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto" data-testid="enquiry-dialog">
      <DialogHeader><DialogTitle className="flex flex-wrap items-center gap-3"><span className="mono text-base">{e.reference}</span> <StatusBadge status={e.status} /></DialogTitle></DialogHeader>
      <DetailGrid rows={[["Type", KINDS[e.kind]], ["Received", fmtDateTime(e.created_at)], ["Name", e.name], ["Company", e.company], ["Email", e.email], ["Phone", e.phone], ["City", e.city], ["Timeline", e.timeline], ["Topic", e.subject], ["Property type", e.property_type], ["Services", e.services?.length ? e.services : ""], ["Sites", e.sites_count], ["Area (sq ft)", e.area_sqft], ["Frequency", e.frequency], ["Systems", e.systems?.length ? e.systems : ""]]} />
      {e.items?.length > 0 && (
        <div className="overflow-x-auto rounded-md border border-slate-200">
          <Table>
            <TableHeader><TableRow><TableHead>Category</TableHead><TableHead>Product</TableHead><TableHead>Qty</TableHead><TableHead>Spec</TableHead></TableRow></TableHeader>
            <TableBody>{e.items.map((it, i) => <TableRow key={i}><TableCell className="text-sm">{it.category}</TableCell><TableCell className="text-sm">{it.description}</TableCell><TableCell className="mono text-sm">{it.quantity}</TableCell><TableCell className="text-sm">{it.specification}</TableCell></TableRow>)}</TableBody>
          </Table>
        </div>
      )}
      {e.message && <div><p className="mono text-[11px] uppercase tracking-wider text-slate-400">Message</p><p className="mt-1 whitespace-pre-wrap text-sm">{e.message}</p></div>}
      <div><p className="mb-2 text-sm font-semibold">Attachments</p><AttachmentList files={e.attachments} /></div>
      {e.notes?.length > 0 && (
        <div><p className="mb-2 text-sm font-semibold">Internal notes</p><ul className="space-y-2">{e.notes.map((n, i) => <li key={i} className="rounded-md bg-[#F4F6F2] p-3 text-sm"><span className="font-semibold">{STATUS[n.status]?.label}</span> · {n.note} <span className="mono text-[11px] text-slate-400">{fmtDateTime(n.at)}</span></li>)}</ul></div>
      )}
      <StatusUpdater endpoint={`/admin/enquiries/${e.id}`} current={e.status} statuses={ENQUIRY_STATUSES} onSaved={onSaved} testid="enquiry-status" />
    </DialogContent>
  </Dialog>
);

export default function AdminEnquiries() {
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [sel, setSel] = useState(null);
  const { data, loading, reload } = useApiData(useQueryPath("/admin/enquiries", { kind, status, search }));
  const rows = data || [];
  return (
    <div data-testid="admin-enquiries-page">
      <PageTitle eyebrow="Leads" title="Enquiries" text="Quick quote, bulk, AMC and contact submissions from the public website. Follow up by email/phone, then update status." action={<ExportButton path={useQueryPath("/admin/export/enquiries", { kind, status, search })} filename={`vinsafe-enquiries-${new Date().toISOString().slice(0, 10)}.csv`} testid="export-enquiries-button" />} />
      <FilterBar search={search} setSearch={setSearch} testid="enquiries-filter">
        {Object.entries(KINDS).map(([k, l]) => <Pill key={k} active={kind === k} onClick={() => setKind(k)} testid={`enquiries-kind-${k}`}>{l}</Pill>)}
        <span className="mx-1 w-px bg-slate-300" />
        {["all", ...ENQUIRY_STATUSES].map((s) => <Pill key={s} active={status === s} onClick={() => setStatus(s)} testid={`enquiries-status-${s}`}>{s === "all" ? "Any status" : STATUS[s].label}</Pill>)}
      </FilterBar>
      {!loading && !rows.length ? <EmptyState title="No enquiries" testid="enquiries-empty" /> : (
        <div className="card-base overflow-x-auto" data-testid="enquiries-table">
          <Table>
            <TableHeader><TableRow><TableHead>Reference</TableHead><TableHead>Type</TableHead><TableHead>From</TableHead><TableHead>Lines / files</TableHead><TableHead>Received</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((e) => (
                <TableRow key={e.id} className="cursor-pointer" onClick={() => setSel(e)} data-testid={`enquiry-row-${e.reference}`}>
                  <TableCell className="mono text-xs font-semibold">{e.reference}</TableCell>
                  <TableCell className="text-xs font-semibold uppercase text-slate-600">{e.kind}</TableCell>
                  <TableCell className="text-sm"><span className="font-medium">{e.company || e.name}</span><div className="text-xs text-slate-500">{e.email}</div></TableCell>
                  <TableCell className="mono text-xs">{e.items?.length || 0} / {e.attachments?.length || 0}</TableCell>
                  <TableCell className="text-sm text-slate-500">{fmtDate(e.created_at)}</TableCell>
                  <TableCell><StatusBadge status={e.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {sel && <EnquiryDialog e={sel} onClose={() => setSel(null)} onSaved={(d) => { setSel(d); reload(); }} />}
    </div>
  );
}
